import { useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { setDeviceId } from 'js/hooks/auth/pas';
import { useDeviceSelector } from 'js/store/hooks';

import { updateDeviceAction } from 'js/hooks/device/store/actions';

export const useDevicePublicAPI = () => {
    const dispatch = useDispatch();
    const device = useDeviceSelector();

    const setId = useCallback((deviceId) => {
        dispatch(updateDeviceAction({ deviceId }));
        setDeviceId(deviceId);
    }, [dispatch]);

    return useMemo(() => {
        const {
            alias,
            browserLanguage,
            browserName,
            browserVersion,
            clientPlatform,
            deliveryPlatform,
            deviceId,
            formFactor,
            isDesktop,
            isInWrapper,
            isLandscape,
            isMobile,
            isOtherBrowser,
            isOtherOs,
            isPortrait,
            isTablet,
            isTouchDevice,
            name,
            orientation,
            os,
            osVersion,
            ua,
            uaOrigin,
            isCrawled
        } = device;

        return {
            /**
             * Permanently set new Device ID.
             * @memberOf Playtech.API.device
             * @function
             * @param {string} id
             * @example
             * Playtech.API.device.setId('e8ff9bed-6d5c-42f1-babc-7a7fec93fb36');
             */
            setId,
            /**
             * Returns currently Device ID.
             * Device ID is stored in local storage.
             * @memberOf Playtech.API.device
             * @function
             * @returns {string}
             * @example
             * // 'e8ff9bed-6d5c-42f1-babc-7a7fec93fb36'
             * Playtech.API.device.getId();
             */
            getId: () => deviceId,
            /**
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'other'
             * Playtech.API.device.alias;
             */
            alias,
            /**
             * Detected browser language.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'en'
             * Playtech.API.device.browserLanguage;
             */
            browserLanguage,
            /**
             * Detected browser name.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'chrome'
             * Playtech.API.device.browserName;
             */
            browserName,
            /**
             * Detected browser version.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // '102'
             * Playtech.API.device.browserVersion;
             */
            browserVersion,
            /**
             * Client platform.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'web'
             * Playtech.API.device.clientPlatform;
             */
            clientPlatform,
            /**
             * Delivery platform.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'Hub2'
             * Playtech.API.device.deliveryPlatform;
             */
            deliveryPlatform,
            /**
             * Device ID.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // '7914165b-9d1e-46fc-ae09-f1e04f438a27'
             * Playtech.API.device.deviceId;
             */
            deviceId,
            /**
             * One of 'mobile', 'tablet' or 'desktop'.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'desktop'
             * Playtech.API.device.formFactor;
             */
            formFactor,
            /**
             * Whether device is desktop.
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // true
             * Playtech.API.device.isDesktop;
             */
            isDesktop,
            /**
             * Whether portal was opened in wrapper.
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // true
             * Playtech.API.device.isInWrapper;
             */
            isInWrapper,
            /**
             * Whether device is in landscape.
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // false
             * Playtech.API.device.isLandscape;
             */
            isLandscape,
            /**
             * Whether device is mobile.
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // false
             * Playtech.API.device.isMobile;
             */
            isMobile,
            /**
             * Whether browser is not 'chrome', 'edge', 'firefox' nor 'safari'.
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // false
             * Playtech.API.device.isOtherBrowser;
             */
            isOtherBrowser,
            /**
             * Whether os is not 'android', 'ios', 'windows' nor 'macintosh'.
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // false
             * Playtech.API.device.isOtherOs;
             */
            isOtherOs,
            /**
             * Whether device is in portrait mode.
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // false
             * Playtech.API.device.isPortrait;
             */
            isPortrait,
            /**
             * Whether device is tablet.
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // false
             * Playtech.API.device.isTablet;
             */
            isTablet,
            /**
             * Whether device supports touch-handlers.
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // false
             * Playtech.API.device.isTouchDevice;
             */
            isTouchDevice,
            /**
             * Detected device name.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'windows'
             * Playtech.API.device.name;
             */
            name,
            /**
             * Device orientation. Could be portrait or landscape.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'portrait'
             * Playtech.API.device.orientation;
             */
            orientation,
            /**
             * Detected device OS.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'windows'
             * Playtech.API.device.os;
             */
            os,
            /**
             * Detected device OS version.
             * @memberOf Playtech.API.device
             * @type {string|number}
             * @example
             * // 11
             * Playtech.API.device.osVersion;
             */
            osVersion,
            /**
             * Browser's user agent string lowercase.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'mozilla/5.0 (windows nt 10.0; win64; x64) applewebkit/537.36 (khtml, like gecko) chrome/102.0.0.0 safari/537.36'
             * Playtech.API.device.ua;
             */
            ua,
            /**
             * Browser's user agent string.
             * @memberOf Playtech.API.device
             * @type {string}
             * @example
             * // 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/128.0.0.0 Safari/537.36'
             * Playtech.API.device.uaOrigin;
             */
            uaOrigin,
            /**
             * @memberOf Playtech.API.device
             * @type {boolean}
             * @example
             * // false
             * Playtech.API.device.isCrawled;
             */
            isCrawled
        };
    }, [setId, device]);
};
